import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  NbMediaBreakpointsService,
  NbMenuService,
  NbSidebarService,
  NbThemeService,
} from '@nebular/theme';
import { LayoutService } from '../../../@core/utils';
import { filter, map, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { ApiService } from '../../../services/api/api.service';

@Component({
  selector: 'ngx-header',
  styleUrls: ['./header.component.scss'],
  templateUrl: './header.component.html',
})
export class HeaderComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();

  storeEnabled = true;
  userPictureOnly = false;

  stores: any[] = [];
  currentStore: any | null = null;

  organizations: Array<{ _id: string; name: string; identifier: string; logo: string | null }> = [];
  activeOrgId: string | null = null;
  organizationName: string | null = null;
  organizationLogo: string | null = null;

  constructor(
    private sidebarService: NbSidebarService,
    private menuService: NbMenuService,
    private themeService: NbThemeService,
    private layoutService: LayoutService,
    private router: Router,
    private breakpointService: NbMediaBreakpointsService,
    private apiService: ApiService,
  ) {}

  ngOnInit(): void {
    const storedStoreState = localStorage.getItem('store_enabled');
    this.storeEnabled = storedStoreState !== null ? storedStoreState === 'true' : true;

    this.fetchUserAndLogo();

    // Re-fetch on every navigation so logo/org-name stay current
    // without needing a full page reload after an org-details save.
    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.fetchUserAndLogo());

    this.apiService.getStoresByUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.stores = res.data.stores || [];
          this.currentStore =
            this.stores.find(s => s.storeType === 'MAIN') ||
            this.stores[0] ||
            null;

          if (this.currentStore) {
            this.storeEnabled = !!this.currentStore.active;
            localStorage.setItem('store_enabled', String(this.storeEnabled));
            localStorage.setItem('current_store_id', this.currentStore._id);
          } else {
            this.storeEnabled = false;
          }
        },
        error: () => {
          this.storeEnabled = false;
        },
      });

    // Load org list — dropdown only renders when user belongs to 2+ orgs
    this.apiService.getUserOrganizations()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.organizations = res?.data ?? [];
          if (this.organizations.length > 1) {
            this.apiService.getMe()
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: (meRes: any) => {
                  this.activeOrgId = meRes?.data?.organizationId ?? null;
                },
              });
          }
        },
        error: () => {
          this.organizations = [];
        },
      });

    const { xl } = this.breakpointService.getBreakpointsMap();

    this.themeService.onMediaQueryChange()
      .pipe(
        map(([, currentBreakpoint]) => currentBreakpoint.width < xl),
        takeUntil(this.destroy$),
      )
      .subscribe(
        (isLessThanXl: boolean) => (this.userPictureOnly = isLessThanXl),
      );

    this.menuService.onItemClick()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // Mobile: fully hide the drawer. Desktop: collapse to icon-only strip.
        if (this.userPictureOnly) {
          this.sidebarService.collapse('menu-sidebar');
        } else {
          this.sidebarService.compact('menu-sidebar');
        }
      });
  }

  toggleSidebar(): boolean {
    this.sidebarService.toggle(true, 'menu-sidebar');
    this.layoutService.changeLayoutSize();
    return false;
  }

  navigateDashboard(): boolean {
    this.router.navigate(['/pages/dashboard']);
    return false;
  }

  logout(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('remember_me');
    localStorage.removeItem('current_store_id');
    localStorage.removeItem('store_enabled');
    this.router.navigate(['/auth/login']);
  }

onStoreToggle(enabled: boolean): void {
  // if (!this.currentStore) {
  //   return;
  // }

  const previousState = this.storeEnabled;
  this.storeEnabled = enabled;

  this.apiService.toggleStoreStatus(this.currentStore._id)
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (res) => {
        this.storeEnabled = res.data.active;
        this.currentStore!.active = res.data.active;
        localStorage.setItem('store_enabled', String(res.data.active));
      },
      error: () => {
        this.storeEnabled = previousState;
      },
    });
}


  onOrgSwitch(selectedOrgId: string): void {
    if (!selectedOrgId || selectedOrgId === this.activeOrgId) return;

    this.apiService.switchOrganization(selectedOrgId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const newToken = res?.data?.token;
          if (newToken) {
            localStorage.setItem('auth_token', newToken);
            this.activeOrgId = selectedOrgId;
            window.location.reload();
          }
        },
        error: () => {
          // Revert dropdown on failure
          const prev = this.activeOrgId;
          this.activeOrgId = null;
          setTimeout(() => { this.activeOrgId = prev; }, 0);
        },
      });
  }

  private fetchUserAndLogo(): void {
    this.apiService.getUserAndLogo()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.organizationName = res.data.organizationName || null;
          this.organizationLogo = res.data.organizationLogo || null;
        },
        error: () => {},
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
